// Optimized version of AboutContainer.js to reduce excessive re-renders
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { updateCustomLabels } from 'apis/cardApi';
import { EditOutlinedIcon, AddIcon, InfoOutlinedIcon } from 'icons/materialIcons';
import { useNavigate, useLocation } from 'react-router-dom';
import { validateUUID } from 'utils/validator';
import { useParams } from 'react-router-dom';
import { useCardContext } from 'contexts/CardContext';
import { useAuth } from 'contexts/AuthContext';
import TeamChooseThemeField from './TeamChooseThemeField';
import TeamChooseFontField from './TeamChooseFontField';
import TeamProfilePhotoCoverPhotoCompanyLogo from './profilePhotoCoverPhotoCompanyLogo/TeamProfilePhotoCoverPhotoCompanyLogo';
import { getCardLayouts, getCardById, updateCard, updateCardProfilePictureUrl, updateCardCompanyLogoUrl, updateCardCoverPhotoUrl } from 'apis/cardApi';
import { uploadImageBlob, deleteImage } from 'apis/imageStorageApi';
import { getContactLinksByCard } from 'apis/contactLinkApi';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import TeamPronounModal from './TeamPronounModal';
import {
  Box, Grid, Typography, IconButton, Button, TextField, Dialog,
  DialogActions, DialogContent, DialogTitle, Tooltip, CircularProgress,
  InputAdornment
} from '@mui/material';

import { DeleteOutlineIcon, LockOutlinedIcon } from 'icons/materialIcons';
import TeamCustomLabelEditor from './TeamCustomLabelEditor';
import { getUserTeams } from 'team/apis/teamApi';
import { getSubteam } from 'team/apis/teamSubteamApi';
import { getTemplates } from 'team/apis/teamTemplateApi';
import { useTeam } from 'team/contexts/teamContext';

// Import RenderDebugger if needed
// import RenderDebugger from 'components/common/RenderDebugger';

const TeamMemberAboutContainer = ({ user, setSelectedItem, template }) => {
  // Use refs to track if we've already performed certain operations
  const initializedRef = useRef(false);
  const fetchedCardDetailsRef = useRef(false);
  const fetchedLayoutOptionsRef = useRef(false);
  const fetchedContactLinksRef = useRef(false);



  // State for form fields
  const [formState, setFormState] = useState({
    cardName: '',
    fullName: '',
    jobTitle: '',
    company: '',
    location: '',
    bio: '',
    theme: '',
    linkColor: '',
    cardLayout: '',
    font: 'Inter'
  });

  // Extract individual state values from formState
  const { cardName, fullName, jobTitle, company, location, bio, theme, linkColor, cardLayout, font } = formState;

  // Other state
  const [loading, setLoading] = useState(false);
  const [isDeletingProfilePicture, setIsDeletingProfilePicture] = useState(false);
  const [isDeletingCoverPhoto, setIsDeletingCoverPhoto] = useState(false);
  const [isDeletingCompanyLogo, setIsDeletingCompanyLogo] = useState(false);
  const { authState } = useAuth();
  const [pronounModalOpen, setPronounModalOpen] = useState(false);
  const [pronoun, setPronoun] = useState('he'); // Default to 'he'
  // Add notification snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showCharLimitNotification, setShowCharLimitNotification] = useState(false);

  const navigate = useNavigate();
  const [profilePictureBlob, setProfilePictureBlob] = useState(null);
  const [coverPhotoBlob, setCoverPhotoBlob] = useState(null);
  const [companyLogoBlob, setCompanyLogoBlob] = useState(null);

  // Add state for custom tab/group labels (local for About form)
  const [customLinkTypeLabels, setCustomLinkTypeLabelsState] = useState({
    'Contact': 'Contact',
    'Social': 'Social',
    'Products & Services': 'Products & Services'
  });

  const userId = authState.user.userId;
  const { setContactLinksForCard } = useContactLinkContext();

  const [openDialog, setOpenDialog] = useState(false);
  const { cardId: initialCardId } = useParams();
  const routerLocation = useLocation();
  const [cardId, setCardId] = useState(initialCardId);

  // ✅ TEMPLATE CONTEXT: Detect if we're in template edit page
  const isTemplateContext = template !== undefined || routerLocation.pathname.includes('/template/');
  const [cardDetails, setCardDetails] = useState(null);
  const [dialogCardName, setDialogCardName] = useState('');
  const [cardLayoutOptions, setCardLayoutOptions] = useState([]);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [contactLinks, setContactLinks] = useState([]);

  // Add state for card design
  const [cardDesign, setCardDesign] = useState('default');

  const {
    cardState,
    setCardFullName,
    setCardJobTitle,
    setCardCompany,
    setCardLocation,
    setCardTheme,
    setCardBio,
    setCardLinkColor,
    setCardProfilePictureUrl,
    setCardCoverPhotoUrl,
    setCardCompanyLogoUrl,
    setContextCardLayout,
    setCardName: contextSetCardName,
    updateCardData,
    setCardPronoun,
    setCustomLinkTypeLabels,
    setCardFont
  } = useCardContext();

  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

  // Subteam restrictions state
  const [restrictions, setRestrictions] = useState({
    about: {
      allowChangeRequests: false,
      lockAll: false,
      fields: {
        'Profile Picture': false,
        'Name': false,
        'Job Title': false,
        'Bio': false,
        'Location': false,
        'Company Logo': false,
        'Card Color': false
      }
    }
  });
  const [loadingRestrictions, setLoadingRestrictions] = useState(false);
  const [isTeamAdmin, setIsTeamAdmin] = useState(false);
  const [isSubteamAdmin, setIsSubteamAdmin] = useState(false);

  // Template lock state - tracks which fields are locked by assigned template
  const [templateLocks, setTemplateLocks] = useState({
    'Profile Picture': false,
    'Cover Photo': false,
    'Company Logo': false,
    'Name': false,
    'Job Title': false,
    'Company': false,
    'Location': false,
    'Bio': false,
    'Card Layout': false,
    'Card Theme': false,
    'Link Color': false,
    'Font': false
  });
  const [assignedTemplate, setAssignedTemplate] = useState(null);
  const { currentTeamId, getCurrentTeam } = useTeam();

  // Determine if we're in the creation flow by checking if cardId is a UUID
  const isCreationFlow = useMemo(() => validateUUID(cardId), [cardId]);

  // Set the button text and action based on whether we're creating or editing
  const submitButtonText = isCreationFlow ? "Next" : "Update";

  // Handle snackbar close
  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // Load subteam restrictions for the current user
  const loadRestrictions = useCallback(async () => {
    if (!userId || isCreationFlow) return; // Skip for new cards or if no user

    setLoadingRestrictions(true);
    try {
      // Get user's team memberships
      const teamsResponse = await getUserTeams();
      const teams = teamsResponse?.teams || teamsResponse || [];

      // Find all subteams the user belongs to
      const subteamIds = [];
      let userIsTeamAdmin = false;
      let userIsSubteamAdmin = false;

      teams.forEach(team => {
        const membership = team.membershipInfo || team;
        if (membership.role === 'admin') {
          userIsTeamAdmin = true;
        }
        if (membership.isSubteamAdmin) {
          userIsSubteamAdmin = true;
        }
        if (membership.subteam?._id) {
          subteamIds.push(membership.subteam._id);
        }
      });

      setIsTeamAdmin(userIsTeamAdmin);
      setIsSubteamAdmin(userIsSubteamAdmin);

      // If user is admin, no restrictions apply
      if (userIsTeamAdmin || userIsSubteamAdmin) {
        setLoadingRestrictions(false);
        return;
      }

      // Load restrictions from all subteams (if any subteam restricts, it's restricted)
      const mergedRestrictions = {
        about: {
          allowChangeRequests: false,
          lockAll: false,
          fields: {
            'Profile Picture': false,
            'Name': false,
            'Job Title': false,
            'Bio': false,
            'Location': false,
            'Company Logo': false,
            'Card Color': false
          }
        }
      };

      for (const subteamId of subteamIds) {
        try {
          const subteamResponse = await getSubteam(subteamId);
          const subteam = subteamResponse?.subteam || subteamResponse;
          const subteamRestrictions = subteam?.settings?.restrictions || {};

          if (subteamRestrictions.about) {
            const aboutRestrictions = subteamRestrictions.about;
            // Merge lockAll (if any subteam has lockAll, it's locked)
            if (aboutRestrictions.lockAll) {
              mergedRestrictions.about.lockAll = true;
            }
            // Merge allowChangeRequests (if any subteam allows it, it's allowed)
            if (aboutRestrictions.allowChangeRequests) {
              mergedRestrictions.about.allowChangeRequests = true;
            }
            // Merge field restrictions (if any subteam restricts a field, it's restricted)
            if (aboutRestrictions.fields) {
              Object.keys(mergedRestrictions.about.fields).forEach(field => {
                if (aboutRestrictions.fields[field] === true) {
                  mergedRestrictions.about.fields[field] = true;
                }
              });
            }
          }
        } catch (error) {
          console.error(`Error loading restrictions from subteam ${subteamId}:`, error);
        }
      }

      setRestrictions(mergedRestrictions);
    } catch (error) {
      console.error('Error loading restrictions:', error);
      // On error, don't restrict (fail open)
    } finally {
      setLoadingRestrictions(false);
    }
  }, [userId, isCreationFlow]);

  // Load restrictions when component mounts
  useEffect(() => {
    loadRestrictions();
  }, [loadRestrictions]);

  // Load template locks from card's applied template (when template is applied via "Assign to template")
  // Also check assigned templates as fallback
  useEffect(() => {
    const loadTemplateLocks = async () => {
      // Skip if in template context or no team/user
      if (isTemplateContext || !currentTeamId || !userId || isCreationFlow) return;

      try {
        // First, check if card has an applied template (from "Assign to template" button)
        if (cardDetails?.card?.appliedTemplateId && cardDetails?.card?.templateLockedFields) {
          // Card has an applied template - use the locked fields from the card
          const lockedFieldsMap = cardDetails.card.templateLockedFields;
          const locks = {
            'Profile Picture': lockedFieldsMap.get?.('Profile Picture') || lockedFieldsMap['Profile Picture'] || false,
            'Cover Photo': lockedFieldsMap.get?.('Cover Photo') || lockedFieldsMap['Cover Photo'] || false,
            'Company Logo': lockedFieldsMap.get?.('Company Logo') || lockedFieldsMap['Company Logo'] || false,
            'Name': false, // Name is always editable (not locked by template)
            'Job Title': lockedFieldsMap.get?.('Job Title') || lockedFieldsMap['Job Title'] || false,
            'Company': lockedFieldsMap.get?.('Company') || lockedFieldsMap['Company'] || false,
            'Location': lockedFieldsMap.get?.('Location') || lockedFieldsMap['Location'] || false,
            'Bio': lockedFieldsMap.get?.('Bio') || lockedFieldsMap['Bio'] || false,
            'Card Layout': lockedFieldsMap.get?.('Card Layout') || lockedFieldsMap['Card Layout'] || false,
            'Card Theme': lockedFieldsMap.get?.('Card Theme') || lockedFieldsMap['Card Theme'] || false,
            'Link Color': lockedFieldsMap.get?.('Link Color') || lockedFieldsMap['Link Color'] || false,
            'Font': lockedFieldsMap.get?.('Font') || lockedFieldsMap['Font'] || false
          };
          setTemplateLocks(locks);
          return; // Use applied template locks, don't check assigned templates
        }

        // Fallback: Check assigned templates (for backwards compatibility)
        const templatesResponse = await getTemplates(currentTeamId);
        if (templatesResponse?.success && templatesResponse?.templates) {
          // Find template where user is assigned
          const userTemplate = templatesResponse.templates.find(template => {
            if (!template.assignedMembers || !Array.isArray(template.assignedMembers)) return false;
            // Check if user is in assignedMembers (could be ObjectId string or populated user object)
            return template.assignedMembers.some(member => {
              // If populated, member will be an object with userId
              if (typeof member === 'object' && member !== null) {
                return member.userId === userId || member._id?.toString() === userId;
              }
              // If it's a string ID, compare directly (though this is less likely after population)
              return member === userId || member.toString() === userId;
            });
          });

          if (userTemplate) {
            setAssignedTemplate(userTemplate);
            const about = userTemplate.about || {};

            // Check which fields are set (non-empty) in template
            // Fields set in template = locked on individual cards
            const locks = {
              'Profile Picture': !!(about.profilePictureUrl && about.profilePictureUrl.trim()),
              'Cover Photo': !!(about.coverPhotoUrl && about.coverPhotoUrl.trim()),
              'Company Logo': !!(about.companyLogoUrl && about.companyLogoUrl.trim()),
              'Name': false, // Name is always editable (not locked by template)
              'Job Title': !!(about.jobTitle && about.jobTitle.trim()),
              'Company': !!(about.company && about.company.trim()),
              'Location': !!(about.location && about.location.trim()),
              'Bio': !!(about.bio && about.bio.trim()),
              'Card Layout': !!(about.cardLayout && about.cardLayout.trim()),
              'Card Theme': !!(about.cardTheme && about.cardTheme.trim()),
              'Link Color': !!(about.linkColor && about.linkColor.trim()),
              'Font': !!(about.font && about.font.trim())
            };

            setTemplateLocks(locks);
          } else {
            // No template assigned, clear locks
            setTemplateLocks({
              'Profile Picture': false,
              'Cover Photo': false,
              'Company Logo': false,
              'Name': false,
              'Job Title': false,
              'Company': false,
              'Location': false,
              'Bio': false,
              'Card Layout': false,
              'Card Theme': false,
              'Link Color': false,
              'Font': false
            });
            setAssignedTemplate(null);
          }
        }
      } catch (error) {
        console.error('Error loading template locks:', error);
        // On error, don't lock fields (fail open)
      }
    };

    loadTemplateLocks();
  }, [currentTeamId, userId, isTemplateContext, isCreationFlow, cardDetails]);

  // Helper functions to check if fields are restricted
  const isFieldRestricted = useCallback((fieldName) => {
    if (isTeamAdmin || isSubteamAdmin) return false; // Admins are not restricted
    if (restrictions.about.lockAll) return true; // If lockAll is enabled, all fields are restricted
    return restrictions.about.fields[fieldName] === true;
  }, [restrictions, isTeamAdmin, isSubteamAdmin]);

  // Check if field is locked by template (set in template = locked)
  const isFieldLockedByTemplate = useCallback((fieldName) => {
    if (isTemplateContext) return false; // Not applicable in template edit context
    return templateLocks[fieldName] === true;
  }, [templateLocks, isTemplateContext]);

  // Combined check: field is restricted if either subteam restriction OR template lock applies
  const isFieldLocked = useCallback((fieldName) => {
    return isFieldRestricted(fieldName) || isFieldLockedByTemplate(fieldName);
  }, [isFieldRestricted, isFieldLockedByTemplate]);

  const canRequestChange = useCallback(() => {
    return restrictions.about.allowChangeRequests === true;
  }, [restrictions]);

  // ✅ CRITICAL FIX: Prevent empty values from overwriting existing card data
  const updateFormState = useCallback((updater) => {
    setFormState(prevState => {
      const newState = typeof updater === 'function' ? updater(prevState) : { ...prevState, ...updater };
      return newState;
    });
  }, []); // ✅ FIXED: Removed all dependencies to prevent infinite loops

  // Reset character limit notification when bio length goes below 250
  useEffect(() => {
    if (bio.length < 250 && showCharLimitNotification) {
      setShowCharLimitNotification(false);
    }
  }, [bio.length, showCharLimitNotification]);





  // Add useEffect to fetch card design
  useEffect(() => {
    const fetchCardDesign = async () => {
      try {
        if (!validateUUID(cardId)) {
          const { success, card } = await getCardById(cardId);
          if (success && card) {
            setCardDesign(card.design || 'design5');
          }
        } else {
          // For new cards, assume design5 as default
          setCardDesign('design5');
        }
      } catch (error) {
        console.error('Error fetching card design:', error);
        setCardDesign('design5'); // Default fallback
      }
    };

    if (cardId) {
      fetchCardDesign();
    }
  }, [cardId]);

  // Optimize fetchContactLinks
  const fetchContactLinks = useCallback(async () => {
    if (!cardId || fetchedContactLinksRef.current) return;

    // ✅ FIX: Skip backend call for new card creation (UUID)
    const isNewCardCreation = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

    if (isNewCardCreation) {
      fetchedContactLinksRef.current = true;
      return;
    }

    try {
      const { success, contactLinks } = await getContactLinksByCard(cardId);
      if (success && Array.isArray(contactLinks)) {
        // Contact links loaded successfully
        setContactLinks(contactLinks);
        setContactLinksForCard(cardId, contactLinks);
      }
      fetchedContactLinksRef.current = true;
    } catch (error) {
      console.error('Error fetching contact links:', error);
    }
  }, [cardId]); // ✅ FIXED: Removed setContactLinksForCard dependency to prevent infinite loops

  // Fetch contact links for the current card
  useEffect(() => {
    fetchContactLinks();
  }, [cardId]); // ✅ FIXED: Only depend on cardId to prevent infinite loops

  // ✅ PRODUCTION FIX: fetchLayoutOptions with stable dependencies
  useEffect(() => {
    const fetchLayoutOptions = async () => {
      if (fetchedLayoutOptionsRef.current || cardDesign === 'design5') return;

      try {
        const layouts = await getCardLayouts();
        setCardLayoutOptions(layouts);

        if (layouts.length > 0 && !formState.cardLayout) {
          const defaultLayout = layouts[0];
          updateFormState({ cardLayout: defaultLayout });
          // Call function directly without dependency
          setContextCardLayout(cardId, defaultLayout);
        }
        fetchedLayoutOptionsRef.current = true;
      } catch (error) {
        console.error('Error fetching layout options:', error);
        setCardLayoutOptions(['left-aligned', 'center-aligned', 'portrait']);

        if (!formState.cardLayout) {
          updateFormState({ cardLayout: 'left-aligned' });
          setContextCardLayout(cardId, 'left-aligned');
        }
      }
    };

    if (cardDesign !== 'design5') {
      fetchLayoutOptions();
    } else {
      if (!formState.cardLayout) {
        updateFormState({ cardLayout: 'left-aligned' });
        setContextCardLayout(cardId, 'left-aligned');
      }
    }
  }, [cardDesign, cardId]); // ✅ Only stable dependencies

  // Load data from context if available (only once) 
  useEffect(() => {
    if (isCreationFlow && !initializedRef.current) {
      // ✅ FIXED: Use a ref to prevent infinite re-renders by only initializing once
      const currentCardState = cardState[cardId];
      if (currentCardState) {
        // Load data from context for a new card being created
        const savedPronoun = currentCardState.cardPronoun || 'he';

        // ✅ FIXED: Use setFormState directly to avoid circular dependency
        setFormState(prevState => ({
          ...prevState,
          cardName: currentCardState.cardName || '',
          fullName: currentCardState.cardFullName || '',
          pronoun: savedPronoun,
          jobTitle: currentCardState.cardJobTitle || '',
          company: currentCardState.cardCompany || '',
          location: currentCardState.cardLocation || '',
          bio: currentCardState.cardBio || '',
          theme: currentCardState.cardTheme || '',
          linkColor: currentCardState.cardLinkColor || '',
          cardLayout: currentCardState.contextCardLayout || 'left-aligned'
        }));

        setPronoun(savedPronoun);

        // Mark as initialized to prevent re-running
        initializedRef.current = true;
        setDataInitialized(true);
      }
    }
  }, [cardId, isCreationFlow, cardState]); // ✅ FIXED: Added cardState but protected by initializedRef

  // ✅ PRODUCTION FIX: Load fresh card data with comprehensive protection


  // Fetch card details only once (fallback for existing logic)
  const fetchCardDetails = useCallback(async () => {
    if (fetchedCardDetailsRef.current || isCreationFlow || !cardId) return;

    try {
      setLoading(true);

      const data = await getCardById(cardId);
      setCardDetails(data);
      setLoading(false);
      fetchedCardDetailsRef.current = true;
    } catch (error) {
      console.error('Error fetching card details:', error);
      setLoading(false);
    }
  }, [cardId, isCreationFlow]);

  // Fetch existing card data for editing (only once)
  useEffect(() => {
    if (!cardDetails?.card) {
      fetchCardDetails();
    }
  }, [cardId, isCreationFlow, cardDetails]); // ✅ FIXED: Removed fetchCardDetails dependency to prevent infinite loops

  // ✅ CRITICAL FIX: Initialize form state without overwriting context
  useEffect(() => {
    if (cardDetails?.card && !isCreationFlow && !initializedRef.current) {


      const savedPronoun = cardDetails.card.contact_info?.pronoun || 'he';

      // ✅ CRITICAL FIX: Don't overwrite context during form initialization
      // Just set local form state - the context already has the correct data from the server
      setFormState(prev => ({
        ...prev,
        cardName: cardDetails.card.cardName || '',
        fullName: cardDetails.card.contact_info?.fullName || '',
        pronoun: savedPronoun,
        jobTitle: cardDetails.card.contact_info?.jobTitle || '',
        company: cardDetails.card.contact_info?.company || '',
        location: cardDetails.card.contact_info?.location || '',
        bio: cardDetails.card.bio || '',
        theme: cardDetails.card.cardTheme || '',
        linkColor: cardDetails.card.linkColor || '',
        cardLayout: cardDetails.card.cardLayout || 'left-aligned',
        font: cardDetails.card.font || 'Inter'
      }));

      setPronoun(savedPronoun);
      setProfilePictureUrl(cardDetails.card.profilePictureUrl || '');
      setCoverPhotoUrl(cardDetails.card.coverPhotoUrl || '');
      setCompanyLogoUrl(cardDetails.card.companyLogoUrl || '');

      // Mark as initialized FIRST
      initializedRef.current = true;
      setDataInitialized(true);

    }
  }, [cardDetails, isCreationFlow]);   // ✅ Removed updateFormState dependency to prevent context overwrites

  // ✅ SYNC LOCAL IMAGE STATE FROM CONTEXT: Update local image state when context changes
  // This ensures that when a template is applied and context is updated, the form fields reflect the changes
  useEffect(() => {
    if (!cardId || isCreationFlow || !initializedRef.current) return;

    const currentCardState = cardState[cardId];
    if (!currentCardState) return;

    // Sync image URLs from context to local state
    // Only update if the context value is different from local state to avoid unnecessary updates
    if (currentCardState.cardProfilePictureUrl !== undefined) {
      const contextProfileUrl = currentCardState.cardProfilePictureUrl || null;
      setProfilePictureUrl(prev => {
        // Only update if different to prevent unnecessary re-renders
        return prev !== contextProfileUrl ? contextProfileUrl : prev;
      });
    }

    if (currentCardState.cardCoverPhotoUrl !== undefined) {
      const contextCoverUrl = currentCardState.cardCoverPhotoUrl || null;
      setCoverPhotoUrl(prev => {
        // Only update if different to prevent unnecessary re-renders
        return prev !== contextCoverUrl ? contextCoverUrl : prev;
      });
    }

    if (currentCardState.cardCompanyLogoUrl !== undefined) {
      const contextLogoUrl = currentCardState.cardCompanyLogoUrl || null;
      setCompanyLogoUrl(prev => {
        // Only update if different to prevent unnecessary re-renders
        return prev !== contextLogoUrl ? contextLogoUrl : prev;
      });
    }

    // ✅ SYNC THEME/LINKCOLOR FROM CONTEXT: Update form state when template is applied
    // This ensures that when a template changes theme/linkColor, the form state reflects the changes
    if (currentCardState.cardTheme !== undefined) {
      setFormState(prev => {
        if (prev.theme !== currentCardState.cardTheme) {
          return { ...prev, theme: currentCardState.cardTheme || '' };
        }
        return prev;
      });
    }

    if (currentCardState.cardLinkColor !== undefined) {
      setFormState(prev => {
        if (prev.linkColor !== currentCardState.cardLinkColor) {
          return { ...prev, linkColor: currentCardState.cardLinkColor || '' };
        }
        return prev;
      });
    }

    if (currentCardState.contextCardLayout !== undefined) {
      setFormState(prev => {
        if (prev.cardLayout !== currentCardState.contextCardLayout) {
          return { ...prev, cardLayout: currentCardState.contextCardLayout || 'left-aligned' };
        }
        return prev;
      });
    }

    if (currentCardState.cardFont !== undefined) {
      setFormState(prev => {
        if (prev.font !== currentCardState.cardFont) {
          return { ...prev, font: currentCardState.cardFont || 'Inter' };
        }
        return prev;
      });
    }

    if (currentCardState.cardBio !== undefined) {
      setFormState(prev => {
        if (prev.bio !== currentCardState.cardBio) {
          return { ...prev, bio: currentCardState.cardBio || '' };
        }
        return prev;
      });
    }
  }, [cardId, cardState, isCreationFlow]); // Removed local state from dependencies to prevent unnecessary re-runs



  // ✅ CRITICAL FIX: Populate context immediately when card details are available
  useEffect(() => {
    if (cardDetails?.card && !isCreationFlow) {
      const card = cardDetails.card;
      const savedPronoun = card.contact_info?.pronoun || 'he';

      // ✅ CRITICAL FIX: Populate context immediately without waiting for initialization
      try {
        setCardPronoun(cardId, savedPronoun);
        setContextCardLayout(cardId, card.cardLayout || 'left-aligned');
        setCardLocation(cardId, card.contact_info?.location || '');
        setCardBio(cardId, card.bio || '');
        setCardFullName(cardId, card.contact_info?.fullName || '');
        setCardJobTitle(cardId, card.contact_info?.jobTitle || '');
        setCardCompany(cardId, card.contact_info?.company || '');
        setCardTheme(cardId, card.cardTheme || '');
        setCardLinkColor(cardId, card.linkColor || '');
        setCardProfilePictureUrl(cardId, card.profilePictureUrl || '');
        setCardCoverPhotoUrl(cardId, card.coverPhotoUrl || '');
        setCardCompanyLogoUrl(cardId, card.companyLogoUrl || '');
        contextSetCardName(cardId, card.cardName || '');
        if (setCardFont) {
          setCardFont(cardId, card.font || 'Inter');
        }

        if (card.customLinkTypeLabels) {
          setCustomLinkTypeLabels(cardId, card.customLinkTypeLabels);
        }
      } catch (error) {
        console.error('🔍 AboutContainer: Error populating context:', error);
      }
    }
  }, [cardDetails, isCreationFlow, cardId]); // ✅ FIXED: Removed context setter dependencies to prevent infinite loops

  // ✅ CRITICAL FIX: Only sync meaningful changes to context, not empty values
  const prevFormStateRef = useRef(null);

  // ✅ TEMPORARILY DISABLED: This useEffect was causing infinite loops by syncing formState to context
  // The handleInputChange function already updates the context directly, making this redundant
  // 
  // useEffect(() => {
  //   if (initializedRef.current && cardId && cardState[cardId] && !isCreationFlow) {
  //     const card = cardState[cardId];
  //     // ... sync logic that was causing infinite re-renders
  //   }
  // }, [cardId, formState, isCreationFlow]);

  // Cleanup ref when cardId changes to prevent stale state
  useEffect(() => {
    prevFormStateRef.current = null;
  }, [cardId]);

  // Optimize handleInputChange to update multiple values at once
  const handleInputChange = useCallback(async (event) => {
    const { name, value } = event.target;

    // Check if field is restricted
    const fieldNameMap = {
      'fullName': 'Name',
      'jobTitle': 'Job Title',
      'company': 'Company',
      'location': 'Location',
      'bio': 'Bio'
    };

    const restrictionFieldName = fieldNameMap[name];

    // Check if field is locked by template first
    if (restrictionFieldName && isFieldLockedByTemplate(restrictionFieldName)) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return; // Prevent update
    }

    // Check if field is restricted by subteam
    if (restrictionFieldName && isFieldRestricted(restrictionFieldName)) {
      // Field is restricted - show notification and prevent update
      setSnackbarMessage(
        canRequestChange()
          ? `"${restrictionFieldName}" is restricted by your subteam. You can submit a change request.`
          : `"${restrictionFieldName}" is restricted by your subteam and cannot be updated.`
      );
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return; // Prevent update
    }

    // Update local state
    updateFormState({ [name]: value });

    // Update context based on field name - use synchronous updates for immediate preview
    try {
      switch (name) {
        case 'fullName':
          await setCardFullName(cardId, value);
          break;
        case 'jobTitle':
          await setCardJobTitle(cardId, value);
          break;
        case 'company':
          await setCardCompany(cardId, value);
          break;
        case 'location':
          await setCardLocation(cardId, value);
          break;
        case 'bio':
          await setCardBio(cardId, value);
          // Show character limit notification when bio reaches 250 characters
          if (value.length === 250 && !showCharLimitNotification) {
            setShowCharLimitNotification(true);
            // Auto-hide notification after 3 seconds
            setTimeout(() => setShowCharLimitNotification(false), 3000);
          }
          break;
        default:
          break;
      }

      // Force immediate context update for preview using updateCardData
      if (cardId) {
        // Create a minimal update object with just the changed field
        const updateData = {};
        switch (name) {
          case 'fullName':
            updateData.cardFullName = value;
            break;
          case 'jobTitle':
            updateData.cardJobTitle = value;
            break;
          case 'company':
            updateData.cardCompany = value;
            break;
          case 'location':
            updateData.cardLocation = value;
            break;
          case 'bio':
            updateData.cardBio = value;
            break;
          default:
            break;
        }

        if (Object.keys(updateData).length > 0) {
          await updateCardData(cardId, updateData, false);
        }
      }
    } catch (error) {
      console.error('🔍 AboutContainer: Error updating context:', error);
    }
  }, [cardId, setCardBio, setCardCompany, setCardFullName, setCardJobTitle, setCardLocation, updateCardData, showCharLimitNotification, isFieldRestricted, isFieldLockedByTemplate, canRequestChange]); // ✅ FIXED: Removed updateFormState dependency

  const handleCardNameClick = useCallback(() => {
    setDialogCardName(cardName);
    setOpenDialog(true);
  }, [cardName]);

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleCardNameChange = useCallback(() => {
    updateFormState({ cardName: dialogCardName });
    contextSetCardName(cardId, dialogCardName);
    setOpenDialog(false);
  }, [cardId, contextSetCardName, dialogCardName]); // ✅ FIXED: Removed updateFormState dependency

  const handleOpenPronounModal = () => {
    setPronounModalOpen(true);
  };

  const handleClosePronounModal = () => {
    setPronounModalOpen(false);
  };

  const handleSavePronoun = (newPronoun) => {
    setPronoun(newPronoun);
    updateFormState({ pronoun: newPronoun });

    // Update the context value
    if (setCardPronoun) {
      setCardPronoun(cardId, newPronoun);
    }
  };

  const handleCardLayoutChange = useCallback((event) => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Card Layout')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      event.target.value = cardLayout; // Reset to current value
      return;
    }

    const newLayout = event.target.value;
    updateFormState({ cardLayout: newLayout });
    setContextCardLayout(cardId, newLayout);
  }, [cardId, setContextCardLayout, isFieldLockedByTemplate, cardLayout]); // ✅ FIXED: Removed updateFormState dependency

  const handleCroppedProfilePictureBlob = useCallback((blob) => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Profile Picture')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setProfilePictureBlob(blob);
    // If it's a URL string (from Team Assets), update the preview URL immediately
    if (typeof blob === 'string') {
      setProfilePictureUrl(blob);
    }
  }, [isFieldLockedByTemplate]);

  const handleCroppedCoverPhotoBlob = useCallback((blob) => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Cover Photo')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setCoverPhotoBlob(blob);
    // If it's a URL string (from Team Assets), update the preview URL immediately
    if (typeof blob === 'string') {
      setCoverPhotoUrl(blob);
    }
  }, [isFieldLockedByTemplate]);

  const handleCroppedCompanyLogoBlob = useCallback((blob) => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Company Logo')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setCompanyLogoBlob(blob);
    // If it's a URL string (from Team Assets), update the preview URL immediately
    if (typeof blob === 'string') {
      setCompanyLogoUrl(blob);
    }
  }, [isFieldLockedByTemplate]);

  const handleProfilePictureUrlDelete = useCallback(() => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Profile Picture')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setIsDeletingProfilePicture(true);
    setProfilePictureBlob(null);
  }, [isFieldLockedByTemplate]);

  const handleCoverPhotoUrlDelete = useCallback(() => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Cover Photo')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setIsDeletingCoverPhoto(true);
    setCoverPhotoBlob(null);
  }, [isFieldLockedByTemplate]);

  const handleCompanyLogoUrlDelete = useCallback(() => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Company Logo')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setIsDeletingCompanyLogo(true);
    setCompanyLogoBlob(null);
  }, [isFieldLockedByTemplate]);

  // Theme and link color handlers
  const handleThemeChange = useCallback((newTheme) => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Card Theme')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    updateFormState({ theme: newTheme });
    setCardTheme(cardId, newTheme);
  }, [cardId, setCardTheme, isFieldLockedByTemplate]); // ✅ FIXED: Removed updateFormState dependency

  const handleLinkColorChange = useCallback((newLinkColor) => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Link Color')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    updateFormState({ linkColor: newLinkColor });
    setCardLinkColor(cardId, newLinkColor);
  }, [cardId, setCardLinkColor, isFieldLockedByTemplate]); // ✅ FIXED: Removed updateFormState dependency

  const handleFontChange = useCallback((newFont) => {
    // Check if field is locked by template
    if (isFieldLockedByTemplate('Font')) {
      setSnackbarMessage('This section comes from a template and is unable to be edited');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    updateFormState({ font: newFont });
    setCardFont(cardId, newFont);
  }, [cardId, setCardFont, isFieldLockedByTemplate]);

  // API handlers 
  const handleProfilePictureUrlSave = useCallback(async (cardId, url) => {
    try {
      const result = await updateCardProfilePictureUrl(cardId, url);
      console.log('Profile Picture updated successfully:', result);
    } catch (error) {
      console.error('Error updating profile picture:', error);
    }
  }, []);

  const handleCoverPhotoUrlSave = useCallback(async (cardId, url) => {
    try {
      const result = await updateCardCoverPhotoUrl(cardId, url);
      console.log('Cover Photo updated successfully:', result);
    } catch (error) {
      console.error('Error updating cover photo:', error);
    }
  }, []);

  const handleCompanyLogoUrlSave = useCallback(async (cardId, url) => {
    try {
      const result = await updateCardCompanyLogoUrl(cardId, url);
      console.log('Company logo updated successfully:', result);
    } catch (error) {
      console.error('Error updating company logo:', error);
    }
  }, []);

  const handleCardUpdate = useCallback(async (cardId) => {
    // Check for restricted fields before submitting
    const restrictedFields = [];
    if (isFieldRestricted('Name') && fullName) restrictedFields.push('Name');
    if (isFieldRestricted('Job Title') && jobTitle) restrictedFields.push('Job Title');
    if (isFieldRestricted('Location') && location) restrictedFields.push('Location');
    if (isFieldRestricted('Bio') && bio) restrictedFields.push('Bio');
    if (isFieldRestricted('Card Color') && theme) restrictedFields.push('Card Color');

    if (restrictedFields.length > 0) {
      const message = canRequestChange()
        ? `The following fields are restricted: ${restrictedFields.join(', ')}. You can submit a change request.`
        : `The following fields are restricted and cannot be updated: ${restrictedFields.join(', ')}`;
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw new Error(message);
    }

    const cardDetails = {
      contact_info: {
        fullName: fullName,
        jobTitle: jobTitle,
        company: company,
        location: location,
        pronoun: pronoun, // Include pronoun
      },
      bio: bio,
      cardLayout: cardLayout,
      cardTheme: theme,
      cardName: cardName || '',
      linkColor: linkColor,
      font: font,
    };

    try {
      const result = await updateCard(cardId, cardDetails);
      console.log('Card updated successfully:', result);
      return result;
    } catch (error) {
      console.error('Error updating card:', error);
      // Check if error is due to restrictions
      if (error.response?.status === 403 && error.response?.data?.message) {
        setSnackbarMessage(error.response.data.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      throw error;
    }
  }, [bio, cardLayout, cardName, company, fullName, jobTitle, linkColor, location, theme, pronoun, font, isFieldRestricted, canRequestChange]);

  // Form submission handler
  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    // Save current form data to context
    const formData = {
      cardName,
      cardFullName: fullName,
      cardJobTitle: jobTitle,
      cardPronoun: pronoun,
      cardCompany: company,
      cardLocation: location,
      cardBio: bio,
      cardTheme: theme,
      cardLinkColor: linkColor,
      contextCardLayout: cardLayout,
      cardFont: font,
      customLinkTypeLabels: customLinkTypeLabels,
    };

    // Card context data is available

    updateCardData(cardId, formData);

    // If this is a new card, navigate to Links page
    if (isCreationFlow) {
      navigate(`/team/members/card/add-card/${cardId}?section=links`);

      // Update selected item in parent component
      if (setSelectedItem) {
        setSelectedItem('links');
      }

      return;
    }

    // Otherwise, this is an existing card - update it in the database
    setLoading(true);

    try {
      if (cardDetails?.card?._id) {
        // Update the card details
        await handleCardUpdate(cardDetails.card._id);
        if (Object.keys(customLinkTypeLabels).length > 0) {
          await updateCustomLabels(cardDetails.card._id, customLinkTypeLabels);
        }
        // Update images if needed
        if (coverPhotoBlob) {
          let newCoverPhotoUrl;
          if (typeof coverPhotoBlob === 'string') {
            newCoverPhotoUrl = coverPhotoBlob;
          } else {
            newCoverPhotoUrl = await uploadImageBlob(
              coverPhotoBlob,
              setCoverPhotoUrl,
              'card',
              'cover',
              cardDetails.card._id
            );
          }
          await handleCoverPhotoUrlSave(cardDetails.card._id, newCoverPhotoUrl);
        }

        if (!coverPhotoBlob && isDeletingCoverPhoto) {
          try {
            setCoverPhotoUrl(null);
            await deleteImage('card', 'cover', cardId);
            await updateCardCoverPhotoUrl(cardId, null);
          } catch (error) {
            console.error('Error removing cover photo:', error);
          }
        }

        // Update company logo if needed
        if (companyLogoBlob) {
          let newCompanyLogoUrl;
          if (typeof companyLogoBlob === 'string') {
            newCompanyLogoUrl = companyLogoBlob;
          } else {
            newCompanyLogoUrl = await uploadImageBlob(
              companyLogoBlob,
              setCompanyLogoUrl,
              'card',
              'logo',
              cardDetails.card._id
            );
          }
          await handleCompanyLogoUrlSave(cardDetails.card._id, newCompanyLogoUrl);
        }

        if (!companyLogoBlob && isDeletingCompanyLogo) {
          try {
            setCompanyLogoUrl(null);
            await deleteImage('card', 'logo', cardId);
            await updateCardCompanyLogoUrl(cardId, null);
          } catch (error) {
            console.error('Error removing company logo:', error);
          }
        }

        // Update profile picture if needed
        if (profilePictureBlob) {
          let newProfilePictureUrl;
          if (typeof profilePictureBlob === 'string') {
            newProfilePictureUrl = profilePictureBlob;
          } else {
            newProfilePictureUrl = await uploadImageBlob(
              profilePictureBlob,
              setProfilePictureUrl,
              'card',
              'profile',
              cardDetails.card._id
            );
          }
          await handleProfilePictureUrlSave(cardDetails.card._id, newProfilePictureUrl);
        }

        if (!profilePictureBlob && isDeletingProfilePicture) {
          try {
            setProfilePictureUrl(null);
            await deleteImage('card', 'profile', cardId);
            await updateCardProfilePictureUrl(cardId, null);
          } catch (error) {
            console.error('Error removing profile picture:', error);
          }
        }

        setSnackbarMessage('Card updated successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error updating card:', error);
      setSnackbarMessage('Failed to update card. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setIsDeletingProfilePicture(false);
      setIsDeletingCoverPhoto(false);
      setIsDeletingCompanyLogo(false);
    }
  }, [bio, cardId, cardLayout, cardName, company, companyLogoBlob, coverPhotoBlob,
    fullName, handleCardUpdate, handleCompanyLogoUrlSave, handleCoverPhotoUrlSave,
    handleProfilePictureUrlSave, isDeletingCompanyLogo, isDeletingCoverPhoto,
    isDeletingProfilePicture, isCreationFlow, jobTitle, linkColor, location,
    navigate, profilePictureBlob, setSelectedItem, theme, updateCardData, customLinkTypeLabels,
    pronoun]);

  // Optional: Add debugger component for development only
  // const debugProps = { cardId, cardLayout, fullName, dataInitialized, loading };

  const formatPronounDisplay = (pronoun) => {
    switch (pronoun) {
      case 'he': return 'he/him';
      case 'she': return 'she/her';
      case 'they': return 'they/them';
      case 'ze': return 'ze/zir';
      case '': return '';
      case 'custom': return 'custom'; // This should rarely happen with the new implementation
      default:
        // For custom pronouns, return the custom value itself
        return pronoun;
    }
  };

  // Custom tab/group label state (local for About form)
  const defaultTabLabels = [
    { key: 'Contact', label: 'Name for contact links' },
    { key: 'Social', label: 'Name for social links' },
    { key: 'Products & Services', label: 'Name for products & services links' },
  ];

  // ✅ DISABLED: Custom labels sync to prevent infinite loops
  // This useEffect was accessing cardState but would cause infinite re-renders
  // Custom labels are now managed through direct updates in handleCustomLabelsChange
  //
  // useEffect(() => {
  //   if (cardState[cardId]) {
  //     const contextLabels = cardState[cardId].customLinkTypeLabels || {};
  //     const mergedLabels = { ... };
  //     setCustomLinkTypeLabelsState(mergedLabels);
  //   }
  // }, [cardId, cardState]);

  const saveCustomLabelsRef = useRef(null);

  useEffect(() => {
    saveCustomLabelsRef.current = async (labels) => {
      try {
        await updateCustomLabels(cardId, labels);
        // Custom labels saved successfully to database
      } catch (error) {
        console.error('Error saving custom labels:', error);
        setSnackbarMessage('Failed to save custom labels');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };
  }, [cardId]);

  const handleCustomLabelsChange = (newLabels) => {
    // Custom labels change requested

    // FIXED: Allow empty values but ensure all three keys exist
    // This allows users to delete content while maintaining the structure
    const completeLabels = {
      'Contact': newLabels['Contact'] !== undefined ? newLabels['Contact'] : 'Contact',
      'Social': newLabels['Social'] !== undefined ? newLabels['Social'] : 'Social',
      'Products & Services': newLabels['Products & Services'] !== undefined ? newLabels['Products & Services'] : 'Products & Services'
    };

    // Setting complete labels for custom link types
    setCustomLinkTypeLabelsState(completeLabels);

    // Update context immediately
    setCustomLinkTypeLabels(cardId, completeLabels);

    // Auto-save for existing cards (not new cards)
    if (!isCreationFlow && saveCustomLabelsRef.current) {
      // Debounce the save
      if (saveCustomLabelsRef.timeout) {
        clearTimeout(saveCustomLabelsRef.timeout);
      }
      saveCustomLabelsRef.timeout = setTimeout(() => {
        saveCustomLabelsRef.current(completeLabels);
      }, 500);
    }
  };

  return (
    <>
      {/* Comment out debugger in production */}
      {/* <RenderDebugger componentName="AboutContainer" props={debugProps} /> */}

      {/* Add TeamNotificationSnackbar component */}
      <TeamNotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        handleClose={handleSnackbarClose}
        severity={snackbarSeverity}
      />

      {/* Character Limit Notification */}
      <TeamNotificationSnackbar
        open={showCharLimitNotification}
        message="Character limit reached (max 250 characters)"
        handleClose={() => setShowCharLimitNotification(false)}
        severity="info"
        duration={3000}
      />

      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: 2,
            backgroundColor: '#ffffff'
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginBottom: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px' }} gutterBottom align="left">About</Typography>
          </Box>

          {/* Content Section */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: 2,
              borderRadius: 1,
            }}
          >
            {/* Form Fields Container */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              {/* Left-aligned Template Name (for templates) or Card Name (for team members) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#4f4f4f" }}>
                  {isTemplateContext ? 'Template Name:' : 'Card Name:'}
                </Typography>
                {isTemplateContext ? (
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#212121" }}>
                    {template?.templateName || 'Template Name'}
                  </Typography>
                ) : (
                  <Button
                    variant="text"
                    onClick={handleCardNameClick}
                    value={cardName}
                    sx={{
                      textTransform: 'none',
                      padding: '4px 8px',
                      width: "150px",
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      backgroundColor: "#f7f7f7",
                      color: "#212121",
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      textAlign: 'left',
                    }}
                    endIcon={<EditOutlinedIcon sx={{ color: '#828282', fontSize: "14px" }} />}
                  >
                    {cardName || 'Card Title'}
                  </Button>
                )}
              </Box>

              {/* Right-aligned Card Layout */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Only show layout dropdown if not Card Design 5 */}
                {cardDesign !== 'design5' && (
                  <>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#4f4f4f", display: 'flex', alignItems: 'center' }}>
                      Card Layout:
                    </Typography>
                    <TextField
                      select
                      SelectProps={{
                        native: true
                      }}
                      value={cardLayout}
                      onChange={handleCardLayoutChange}
                      sx={{
                        fontSize: '0.7rem',
                        minWidth: 120,
                        backgroundColor: "#f7f7f7",
                        color: "#212121",
                        fontWeight: 600,
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: "none",
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: "none",
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: "none",
                        },
                      }}
                    >
                      {cardLayoutOptions.map((layout) => (
                        <option key={layout} value={layout}>
                          {layout}
                        </option>
                      ))}
                    </TextField>
                  </>
                )}
              </Box>
            </Box>

            {/* Info section for template context */}
            {isTemplateContext && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  marginTop: 2,
                  marginBottom: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#4f4f4f',
                    lineHeight: 1.5,
                  }}
                >
                  Any field or image left empty below can be set and edited by the individual profile
                </Typography>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ marginBottom: 1, fontSize: '0.75rem' }}>
                        Any fields or images left empty can be edited on the cards assigned to the template. Fields or images coming from the template will be locked on the assigned cards.
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        For example, if you set a company logo individuals will NOT be able to override it. However not setting a company logo will allow anyone assigned to this template to upload their own.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon sx={{ fontSize: '18px', color: '#828282', flexShrink: 0, cursor: 'help' }} />
                </Tooltip>
              </Box>
            )}

            <TeamProfilePhotoCoverPhotoCompanyLogo
              onCroppedProfilePictureBlob={handleCroppedProfilePictureBlob}
              onCroppedCoverPhotoBlob={handleCroppedCoverPhotoBlob}
              onCroppedCompanyLogoBlob={handleCroppedCompanyLogoBlob}
              onDeleteProfilePicture={handleProfilePictureUrlDelete}
              onDeleteCoverPhoto={handleCoverPhotoUrlDelete}
              onDeleteCompanylogo={handleCompanyLogoUrlDelete}
              cardLayout={cardLayout}
              cardDesign={cardDesign}
              isProfilePictureRestricted={isFieldLocked('Profile Picture')}
              isCoverPhotoRestricted={isFieldLocked('Cover Photo')}
              isCompanyLogoRestricted={isFieldLocked('Company Logo')}
              canRequestChange={canRequestChange()}
              onRestrictionNotification={(message) => {
                setSnackbarMessage(message);
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
              }}
            />

            {/* Name, Location, Job Title, Company, and Bio Inputs */}
            <Grid container spacing={2} sx={{ marginTop: 4 }}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: "10px" }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                    Name
                  </Typography>
                  {isFieldLocked('Name') && (
                    <Tooltip title={isFieldLockedByTemplate('Name') ? "This field is locked by your assigned template" : "This field is restricted by your subteam"}>
                      <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: '#999' }} />
                    </Tooltip>
                  )}
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Name"
                  name="fullName"
                  value={fullName}
                  required
                  disabled={isFieldLocked('Name')}
                  onChange={handleInputChange}
                  /* PRONOUN FUNCTIONALITY - COMMENTED OUT FOR TEMPORARY HIDING */
                  /*
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          onClick={handleOpenPronounModal}
                          sx={{
                            textTransform: 'none',
                            minWidth: 'auto',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            backgroundColor: '#e8e8e8',
                            color: '#555555',
                            '&:hover': {
                              backgroundColor: '#d8d8d8',
                            }
                          }}
                          startIcon={<EditOutlinedIcon sx={{ fontSize: '0.85rem' }} />}
                        >
                          {formatPronounDisplay(pronoun)}
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  */
                  sx={{
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '& input::placeholder': {
                        fontSize: '0.8rem',
                        fontWeight: '300',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: "10px" }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                    Location
                  </Typography>
                  {isFieldLocked('Location') && (
                    <Tooltip title={isFieldLockedByTemplate('Location') ? "This field is locked by your assigned template" : "This field is restricted by your subteam"}>
                      <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: '#999' }} />
                    </Tooltip>
                  )}
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Location"
                  name="location"
                  value={location}
                  disabled={isFieldLocked('Location')}
                  onChange={handleInputChange}
                  sx={{
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '& input::placeholder': {
                        fontSize: '0.8rem',
                        fontWeight: '300',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: "10px", marginTop: "5px" }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                    Job Title
                  </Typography>
                  {isFieldLocked('Job Title') && (
                    <Tooltip title={isFieldLockedByTemplate('Job Title') ? "This field is locked by your assigned template" : "This field is restricted by your subteam"}>
                      <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: '#999' }} />
                    </Tooltip>
                  )}
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Job Title"
                  name="jobTitle"
                  value={jobTitle}
                  disabled={isFieldLocked('Job Title')}
                  onChange={handleInputChange}
                  sx={{
                    backgroundColor: '#f7f7f7',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '& input::placeholder': {
                        fontSize: '0.8rem',
                        fontWeight: '300',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: "10px", marginTop: "5px" }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                    Company
                  </Typography>
                  {isFieldLocked('Company') && (
                    <Tooltip title={isFieldLockedByTemplate('Company') ? "This field is locked by your assigned template" : "This field is restricted by your subteam"}>
                      <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: '#999' }} />
                    </Tooltip>
                  )}
                </Box>
                <Box
                  onClick={() => {
                    if (isFieldLocked('Company') && isFieldLockedByTemplate('Company')) {
                      setSnackbarMessage('This section comes from a template and is unable to be edited');
                      setSnackbarSeverity('warning');
                      setSnackbarOpen(true);
                    }
                  }}
                  sx={{
                    cursor: isFieldLocked('Company') ? 'not-allowed' : 'default',
                    pointerEvents: 'auto'
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Company"
                    name="company"
                    value={company}
                    disabled={isFieldLocked('Company')}
                    onChange={handleInputChange}
                    sx={{
                      backgroundColor: '#f7f7f7',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                        '& input::placeholder': {
                          fontSize: '0.8rem',
                          fontWeight: '300',
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Bio Field - move before theme */}
            <Box sx={{ marginTop: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: "10px" }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                  Bio
                </Typography>
                {isFieldLocked('Bio') && (
                  <Tooltip title={isFieldLockedByTemplate('Bio') ? "This field is locked by your assigned template" : "This field is restricted by your subteam"}>
                    <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: '#999' }} />
                  </Tooltip>
                )}
              </Box>
              <TextField
                fullWidth
                name="bio"
                value={bio}
                disabled={isFieldLocked('Bio')}
                onChange={handleInputChange}
                multiline
                minRows={2}
                maxRows={8}
                variant="outlined"
                inputProps={{
                  maxLength: 250
                }}
                sx={{
                  backgroundColor: '#f7f7f7',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                    '& input::placeholder': {
                      fontSize: '0.8rem',
                      fontWeight: '300',
                    },
                  },
                }}
              />
              {/* Character Counter */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 1
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: bio.length >= 250 ? '#1976d2' : '#828282',
                    fontSize: '0.75rem',
                    fontWeight: bio.length >= 250 ? 600 : 400
                  }}
                >
                  {bio.length}/250 characters
                </Typography>
              </Box>
            </Box>

            <TeamChooseThemeField
              theme={theme}
              linkColor={linkColor}
              onThemeChange={handleThemeChange}
              onLinkColorChange={handleLinkColorChange}
              isCardColorRestricted={isFieldLocked('Card Theme')}
              canRequestChange={canRequestChange()}
              onRestrictionNotification={(message) => {
                setSnackbarMessage(message);
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
              }}
            />

            <TeamChooseFontField
              selectedFont={font}
              onFontChange={handleFontChange}
            />

            {/* CustomLabelEditor for custom labels, no heading */}
            <Box sx={{ mt: 3 }}>
              <TeamCustomLabelEditor
                labels={customLinkTypeLabels}
                onChange={handleCustomLabelsChange}
                defaultLabels={defaultTabLabels}
                loading={loading}
              />
            </Box>
          </Box>

          {/* Footer Section */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, marginTop: 'auto' }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              sx={{
                backgroundColor: '#ffffff',
                borderColor: '#dedede',
                border: "0.75px solid",
                padding: '5px 16px',
                color: '#212121',
                textTransform: 'none',
                borderRadius: '10px',
                marginRight: "4px",
                '&:hover': {
                  backgroundColor: '#f7f7f7',
                  borderColor: '#dedede',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              type='submit'
              disabled={loading}
              sx={{
                backgroundColor: '#000000',
                padding: '5px 32px',
                color: '#ffffff',
                fontWeight: "bold",
                textTransform: 'none',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: '#333333',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : submitButtonText}
            </Button>
          </Box>

          {/* Dialog for entering the card name */}
          <Dialog open={openDialog} onClose={handleDialogClose}>
            <DialogTitle>Enter Card Name</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Card Name"
                type="text"
                fullWidth
                variant="outlined"
                name="cardName"
                placeholder='Card Title'
                value={dialogCardName}
                onChange={(e) => setDialogCardName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} color="primary">
                Cancel
              </Button>
              <Button onClick={handleCardNameChange} color="primary" variant="contained">
                Update
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </form>

      {/* PRONOUN MODAL - COMMENTED OUT FOR TEMPORARY HIDING */}
      {/*
      <TeamPronounModal
        open={pronounModalOpen}
        onClose={handleClosePronounModal}
        currentPronoun={pronoun}
        onSave={handleSavePronoun}
      />
      */}
    </>
  );
};

export default React.memo(TeamMemberAboutContainer);